import React, { useEffect, useState } from "react"
import { connect } from "react-redux"
import {
  Card,
  List,
  Tag,
  Typography,
  Badge,
  Space,
  Button,
  Empty,
  Spin,
  Tooltip,
  Avatar,
  Skeleton,
  Modal,
  Divider,
  Row,
  Col,
  message,
} from "antd"
import {
  BellOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  NotificationOutlined,
  MessageOutlined,
  SettingOutlined,
  AlertOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  PaperClipOutlined,
  RightOutlined,
  ReloadOutlined,
} from "@ant-design/icons"
import { Telegram } from "@mui/icons-material"
import { withTranslation } from "react-i18next"
import { getNotifications, markNotificationAsSeen } from "store/actions"
import DocumentFiles, { EntityType } from "components/Common/DocumentFiles"
import { getApiUrlNotificationFiles } from "common/global"
import "./NotificationPanel.css"
import { getCurrentUser } from "helpers/service_helper"

const { Title, Text, Paragraph } = Typography

// Notification Types Configuration
const notificationTypeConfig = {
  1: { label: "اطلاعیه", color: "#1890ff", bgColor: "#e6f7ff", icon: <NotificationOutlined /> },
  2: { label: "اخبار", color: "#13c2c2", bgColor: "#e6fffb", icon: <InfoCircleOutlined /> },
  3: { label: "اخطاریه", color: "#fa8c16", bgColor: "#fff7e6", icon: <WarningOutlined /> },
  4: { label: "هشدار", color: "#ff4d4f", bgColor: "#fff2f0", icon: <AlertOutlined /> },
  5: { label: "پیام", color: "#52c41a", bgColor: "#f6ffed", icon: <MessageOutlined /> },
  6: { label: "پیام سیستمی", color: "#722ed1", bgColor: "#f9f0ff", icon: <SettingOutlined /> },
}

// Status Configuration
const statusConfig = {
  1: { label: "جدید", color: "processing", icon: <ClockCircleOutlined /> },
  2: { label: "تحویل شده", color: "warning", icon: <ClockCircleOutlined /> },
  3: { label: "مشاهده شده", color: "success", icon: <CheckCircleOutlined /> },
}

// Helper function to convert URLs in text to clickable links
const linkifyText = (text) => {
  if (!text) return ""
  const urlRegex = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/g
  return text.replace(urlRegex, (url) => 
    `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #1890ff; text-decoration: underline;">${url}</a>`
  )
}

const NotificationPanel = ({
  telegramLink,
  showTelegram = true,
  lstNotifications,
  loading,
  onGetNotifications,
  onMarkNotificationAsSeen,
  maxItems = 5,
}) => {
  const [viewModalVisible, setViewModalVisible] = useState(false)
  const [selectedNotification, setSelectedNotification] = useState(null)

  useEffect(() => {
    onGetNotifications()
  }, [])

  // Backend already filters by user role, just sort and limit
  const filteredNotifications = lstNotifications
    ?.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate))
    ?.slice(0, maxItems) || []

  const unreadCount = filteredNotifications.filter(n => n.status !== 3).length

  const getTypeConfig = type => notificationTypeConfig[type] || notificationTypeConfig[1]
  const getStatusConfig = status => statusConfig[status] || statusConfig[1]

  const formatDate = dateString => {
    if (!dateString) return ""
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return "همین الان"
    if (diffMins < 60) return `${diffMins} دقیقه پیش`
    if (diffHours < 24) return `${diffHours} ساعت پیش`
    if (diffDays < 7) return `${diffDays} روز پیش`
    return date.toLocaleDateString("fa-IR")
  }

  const handleViewNotification = notification => {
    setSelectedNotification(notification)
    setViewModalVisible(true)
    // Mark as seen if not already
    if (notification.status !== 3) {
      onMarkNotificationAsSeen({ id: notification.id })
    }
  }

  const handleRefresh = () => {
    onGetNotifications()
  }

  const handleDownload = async fileRecord => {
    if (!fileRecord?.s3Path) return

    const authUser = JSON.parse(localStorage.getItem("authUser"))
    const token = authUser?.accessToken || ""

    try {
      const response = await fetch(getApiUrlNotificationFiles(fileRecord.s3Path), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error("Download failed")
      }

      const blob = await response.blob()
      const fileUrl = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = fileUrl
      link.download = fileRecord.fileName || "file"
      link.target = "_blank"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(fileUrl)
    } catch (error) {
      message.error("خطا در دانلود فایل")
    }
  }

  return (
    <>
      <Card
        className="notification-panel-card notification-panel-modern"
        title={
          <Space>
            <Badge count={unreadCount} offset={[10, 0]} size="small">
              <Avatar
                style={{
                  backgroundColor: "#1890ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                icon={<BellOutlined />}
                size={36}
              />
            </Badge>
            <div>
              <Title level={5} style={{ margin: 0 }}>
                اطلاعیه‌ها و هشدارها
              </Title>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {filteredNotifications.length} اطلاعیه
              </Text>
            </div>
          </Space>
        }
        bordered={false}
        style={{
          borderRadius: 12,
          height: "100%",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        }}
        extra={
          <Space>
            <Tooltip title="بروزرسانی">
              <Button
                type="text"
                icon={<ReloadOutlined spin={loading} />}
                onClick={handleRefresh}
                disabled={loading}
              />
            </Tooltip>
            {getCurrentUser().role === "Administrator" && (
            <Button type="link" size="small" href="/notifications">
              مشاهده همه
              <RightOutlined style={{ fontSize: 10, marginRight: 4 }} />
            </Button>
            )
            }
          </Space>
        }
      >
        <Spin spinning={loading}>
          {filteredNotifications.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="اطلاعیه‌ای یافت نشد"
              style={{ padding: "40px 0" }}
            />
          ) : (
            <List
              itemLayout="vertical"
              dataSource={filteredNotifications}
              renderItem={item => {
                const typeConfig = getTypeConfig(item.notificationType)
                const isUnread = item.status !== 3
                const itemDocuments = Array.isArray(item.documents) ? item.documents : []
                
                return (
                  <List.Item
                    className={`notification-item ${isUnread ? "unread" : ""}`}
                    style={{
                      padding: "12px 16px",
                      margin: "8px 0",
                      borderRadius: 8,
                      background: isUnread ? typeConfig.bgColor : "#fafafa",
                      border: `1px solid ${isUnread ? typeConfig.color + "40" : "#f0f0f0"}`,
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                    }}
                    onClick={() => handleViewNotification(item)}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                      <Avatar
                        style={{
                          backgroundColor: typeConfig.bgColor,
                          color: typeConfig.color,
                          flexShrink: 0,
                        }}
                        icon={typeConfig.icon}
                        size={40}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                          <Space size={4}>
                            <Text strong style={{ fontSize: 14 }}>
                              {item.title}
                            </Text>
                            {isUnread && (
                              <Badge status="processing" />
                            )}
                          </Space>
                          <Text type="secondary" style={{ fontSize: 11, flexShrink: 0 }}>
                            {formatDate(item.createdDate)}
                          </Text>
                        </div>
                        <div
                          style={{ 
                            margin: 0, 
                            color: "#666", 
                            fontSize: 13,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                          dangerouslySetInnerHTML={{ __html: linkifyText(item.message) }}
                        />
                        <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Space size={4}>
                            <Tag
                              color={typeConfig.color}
                              style={{ margin: 0, fontSize: 11 }}
                            >
                              {item.notificationTypeName || typeConfig.label}
                            </Tag>
                            {itemDocuments.length > 0 &&
                              itemDocuments.map((doc, index) => (
                                <Tag
                                  key={doc.id || `${item.id}-doc-${index}`}
                                  icon={<PaperClipOutlined />}
                                  style={{ margin: 0, fontSize: 11, cursor: "pointer" }}
                                  onClick={event => {
                                    event.stopPropagation()
                                    handleDownload(doc)
                                  }}
                                >
                                  {`پیوست ${index + 1}`}
                                </Tag>
                              ))}
                            {itemDocuments.length === 0 && item.documentCount > 0 && (
                              <Tag icon={<PaperClipOutlined />} style={{ margin: 0, fontSize: 11 }}>
                                {item.documentCount} فایل
                              </Tag>
                            )}
                          </Space>
                          <Button
                            type="text"
                            size="small"
                            icon={<EyeOutlined />}
                            style={{ color: typeConfig.color }}
                          >
                            مشاهده
                          </Button>
                        </div>
                      </div>
                    </div>
                  </List.Item>
                )
              }}
            />
          )}
        </Spin>

        {showTelegram && telegramLink && getCurrentUser().role === "Executor" && (
          <Card
            size="small"
            style={{
              marginTop: 16,
              background: "linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)",
              borderColor: "#91d5ff",
              borderRadius: 8,
            }}
          >
            <Space>
              <Avatar
                style={{ backgroundColor: "#0088cc" }}
                icon={<Telegram style={{ fontSize: 16 }} />}
                size={32}
              />
              <div>
                <Text strong style={{ display: "block", fontSize: 13 }}>
                  کانال تلگرام
                </Text>
                <a
                  href={telegramLink.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#0088cc", fontSize: 12 }}
                >
                  {telegramLink.text}
                </a>
              </div>
            </Space>
          </Card>
        )}
      </Card>

      {/* View Notification Modal */}
      <Modal
        title={
          <Space>
            {selectedNotification && getTypeConfig(selectedNotification.notificationType).icon}
            <span>جزئیات اطلاعیه</span>
          </Space>
        }
        open={viewModalVisible}
        onCancel={() => {
          setViewModalVisible(false)
          setSelectedNotification(null)
        }}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            بستن
          </Button>,
        ]}
        width={600}
        destroyOnClose
      >
        {selectedNotification && (
          <div>
            <div
              style={{
                background: getTypeConfig(selectedNotification.notificationType).bgColor,
                padding: 16,
                borderRadius: 8,
                marginBottom: 16,
              }}
            >
              <Title level={4} style={{ margin: 0, color: getTypeConfig(selectedNotification.notificationType).color }}>
                {selectedNotification.title}
              </Title>
              <Space style={{ marginTop: 8 }}>
                <Tag color={getTypeConfig(selectedNotification.notificationType).color}>
                  {selectedNotification.notificationTypeName}
                </Tag>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {formatDate(selectedNotification.createdDate)}
                </Text>
              </Space>
            </div>

            <div 
              style={{ fontSize: 14, lineHeight: 2, whiteSpace: "pre-wrap" }}
              dangerouslySetInnerHTML={{ __html: linkifyText(selectedNotification.message) }}
            />

            {selectedNotification.documentCount > 0 && (
              <>
                <Divider orientation="right">
                  <Space>
                    <PaperClipOutlined />
                    فایل‌های پیوست
                  </Space>
                </Divider>
                <DocumentFiles
                  entityId={selectedNotification.id}
                  entityType={EntityType.Notification}
                  readOnly={true}
                  showTitle={false}
                  maxFiles={5}
                />
              </>
            )}
          </div>
        )}
      </Modal>
    </>
  )
}

const mapStateToProps = ({ Notifications }) => ({
  lstNotifications: Notifications.lstNotifications,
  loading: Notifications.loading,
})

const mapDispatchToProps = {
  onGetNotifications: getNotifications,
  onMarkNotificationAsSeen: markNotificationAsSeen,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(NotificationPanel))
